import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import useAuthStore from '@/stores/authStore';
import { Submission, Form } from '@/types';

// Security context to ensure data isolation at the React component level
interface OrganizationSecurityContextType {
  organizationId: string | null;
  validateFormAccess: (form: Form) => boolean;
  validateSubmissionAccess: (submission: Submission) => boolean;
  validateDataArray: <T extends { organizationId?: string }>(data: T[]) => T[];
  securityLevel: 'high' | 'medium' | 'low';
  logSecurityEvent: (event: string, details?: any) => void;
}

const OrganizationSecurityContext = createContext<OrganizationSecurityContextType | undefined>(undefined);

interface OrganizationSecurityProviderProps {
  children: ReactNode;
}

export function OrganizationSecurityProvider({ children }: OrganizationSecurityProviderProps) {
  const { user, organization } = useAuthStore();
  const [securityLevel] = useState<'high' | 'medium' | 'low'>('high'); // Always use highest security
  
  const organizationId = organization?.id || user?.organizationId || null;

  const logSecurityEvent = (event: string, details?: any) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      event,
      organizationId,
      userId: user?.id,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    console.warn(`[SECURITY] ${event}`, logEntry);
    
    // In production, this would send to security monitoring service
    if (import.meta.env.PROD) {
      // TODO: Send to security monitoring service
      // securityMonitor.logEvent(logEntry);
    }
  };

  const validateFormAccess = (form: Form): boolean => {
    if (!organizationId) {
      logSecurityEvent('ACCESS_DENIED_NO_ORG', { 
        resource: 'form', 
        resourceId: form.id,
        reason: 'No organization context' 
      });
      return false;
    }

    if (!form.organizationId) {
      logSecurityEvent('ACCESS_DENIED_NO_FORM_ORG', { 
        resource: 'form', 
        resourceId: form.id,
        reason: 'Form has no organizationId' 
      });
      return false;
    }

    // Allow access if form belongs to user's organization OR directly to the user
    const userCanAccess = form.organizationId === organizationId || form.organizationId === user?.id;
    
    if (!userCanAccess) {
      logSecurityEvent('ACCESS_DENIED_ORG_MISMATCH', { 
        resource: 'form', 
        resourceId: form.id,
        userOrganizationId: organizationId,
        userId: user?.id,
        resourceOrganizationId: form.organizationId,
        reason: 'Organization mismatch - potential data breach attempt'
      });
      return false;
    }

    return true;
  };

  const validateSubmissionAccess = (submission: Submission): boolean => {
    if (!organizationId) {
      logSecurityEvent('ACCESS_DENIED_NO_ORG', { 
        resource: 'submission', 
        resourceId: submission.id,
        reason: 'No organization context' 
      });
      return false;
    }

    if (!submission.organizationId) {
      logSecurityEvent('ACCESS_DENIED_NO_SUBMISSION_ORG', { 
        resource: 'submission', 
        resourceId: submission.id,
        reason: 'Submission has no organizationId' 
      });
      return false;
    }

    // Allow access if submission belongs to user's organization OR directly to the user
    const userCanAccess = submission.organizationId === organizationId || submission.organizationId === user?.id;
    
    if (!userCanAccess) {
      logSecurityEvent('ACCESS_DENIED_ORG_MISMATCH', { 
        resource: 'submission', 
        resourceId: submission.id,
        userOrganizationId: organizationId,
        userId: user?.id,
        resourceOrganizationId: submission.organizationId,
        reason: 'Organization mismatch - potential data breach attempt'
      });
      return false;
    }

    return true;
  };

  const validateDataArray = <T extends { organizationId?: string }>(data: T[]): T[] => {
    if (!organizationId) {
      logSecurityEvent('DATA_FILTER_NO_ORG', { 
        inputCount: data.length,
        reason: 'No organization context for filtering' 
      });
      return [];
    }

    const filteredData = data.filter(item => {
      if (!item.organizationId) {
        logSecurityEvent('DATA_ITEM_NO_ORG', { 
          reason: 'Data item has no organizationId' 
        });
        return false;
      }

      if (item.organizationId !== organizationId) {
        logSecurityEvent('DATA_ITEM_FILTERED', { 
          userOrganizationId: organizationId,
          itemOrganizationId: item.organizationId,
          reason: 'Filtered out data from different organization' 
        });
        return false;
      }

      return true;
    });

    if (filteredData.length !== data.length) {
      logSecurityEvent('DATA_FILTERED', { 
        inputCount: data.length,
        outputCount: filteredData.length,
        filteredCount: data.length - filteredData.length
      });
    }

    return filteredData;
  };

  // Monitor for organization changes and log security events
  useEffect(() => {
    if (organizationId) {
      logSecurityEvent('ORG_CONTEXT_INITIALIZED', { organizationId });
    }
  }, [organizationId]);

  // Security validation on mount
  useEffect(() => {
    if (!user) {
      logSecurityEvent('SECURITY_CONTEXT_NO_USER', {});
    } else if (!organizationId) {
      logSecurityEvent('SECURITY_CONTEXT_NO_ORG', { userId: user.id });
    }
  }, [user, organizationId]);

  const value: OrganizationSecurityContextType = {
    organizationId,
    validateFormAccess,
    validateSubmissionAccess,
    validateDataArray,
    securityLevel,
    logSecurityEvent,
  };

  return (
    <OrganizationSecurityContext.Provider value={value}>
      {children}
    </OrganizationSecurityContext.Provider>
  );
}

export function useOrganizationSecurity() {
  const context = useContext(OrganizationSecurityContext);
  if (context === undefined) {
    throw new Error('useOrganizationSecurity must be used within an OrganizationSecurityProvider');
  }
  return context;
}

// Security hook for validating data arrays
export function useSecureData<T extends { organizationId?: string }>(data: T[]): T[] {
  const { validateDataArray } = useOrganizationSecurity();
  return validateDataArray(data);
}

// Security hook for forms
export function useSecureForms(forms: Form[]): Form[] {
  const { validateFormAccess } = useOrganizationSecurity();
  return forms.filter(validateFormAccess);
}

// Security hook for submissions
export function useSecureSubmissions(submissions: Submission[]): Submission[] {
  const { validateSubmissionAccess } = useOrganizationSecurity();
  return submissions.filter(validateSubmissionAccess);
}